import { createContext, use, useMemo, ReactNode } from 'react';
import { useFileStatus } from '../hooks/use-file-status';
import { useOps } from '../../../hooks/useOps';
import type { UploadConfig, UploadContextValue } from '../types';

const UploadContext = createContext<UploadContextValue | null>(null);

interface UploadProviderProps {
  children: ReactNode;
  config: UploadConfig;
}

export function UploadProvider({ children, config }: UploadProviderProps) {
  const { getCurrentOpsConfig } = useOps();
  const opsConfig = getCurrentOpsConfig();
  const dbName = opsConfig?.dbName;

  const { files, stats, isLoading, isRefetching, error, refetch, invalidate } = useFileStatus({
    docType: config.docType,
    enabled: Boolean(dbName),
  });

  const contextValue = useMemo<UploadContextValue>(
    () => ({
      state: {
        files,
        isLoading,
        isRefetching,
        error,
      },
      actions: {
        refetch,
        invalidate,
      },
      meta: {
        stats,
        config,
        dbName,
      },
    }),
    [files, isLoading, isRefetching, error, refetch, invalidate, stats, config, dbName]
  );

  return <UploadContext value={contextValue}>{children}</UploadContext>;
}

export function useUploadContext(): UploadContextValue {
  const context = use(UploadContext);

  if (!context) {
    throw new Error('useUploadContext must be used within an UploadProvider');
  }

  return context;
}

export { UploadContext };
